import { useState } from 'react'
import {
  Box,
  Button,
  Flex,
  Heading,
  Message,
  Select,
  Text,
  RadioGroup,
  Textarea,
  BaseLink,
  Checkbox,
} from '@workwhile/ui'
import { formatInTimeZone } from 'date-fns-tz'
import { User, WorkShift } from 'typings/common_defs'
import { getLocationTimezone } from 'lib/time'
import {
  DEFAULT_CANCELLATION_POLICY_WINDOW,
  useCancellationPolicy,
} from 'pages/HomePage/ShiftCard/useCancellationPolicy'
import { TimelineShift } from 'api/shift'
import { useRemoveWorker } from 'queries/assignment/useRemoveWorker'
import { FeatureGates } from 'lib/statsig/feature_gates'
import { useGateValue } from '@statsig/react-bindings'
import { Settings } from 'lucide-react'
import { useAssignmentDetailContextStateValue } from 'pages/AssignmentDetailPage/context'
import { OrientationWarningMessage } from './OrientationWarningMessage'
import {
  formatPolicyWindowListCharge,
  formatPolicyWindowRemoveWorkerCharge,
  getPolicyWindowKey,
} from 'lib/cancellationPolicy'

const MIN_REASON_CHARACTERS = 25
const WORKER_TERMINATION_REASONS = [
  { label: 'Worker asked to be removed', value: 'worker_requested' },
  { label: 'Change in headcount needs', value: 'headcount_changes' },
  { label: 'Not a good fit for the assignment', value: 'bad_fit' },
  { label: 'Attendance issues', value: 'attendance_issues' },
  { label: 'Other', value: 'other' },
] as const

const policyUrl =
  'https://storage.googleapis.com/workwhile-terms-docs/static/ww-customer-agreement/ww_customer_agreement_20201204.pdf'

interface RemoveWorkerStepProps {
  assignmentId: number
  worker?: User
  shift?: WorkShift
  onClose: () => void
}

export const RemoveWorkerStep = ({
  assignmentId,
  worker,
  shift,
  onClose,
}: RemoveWorkerStepProps) => {
  const [propagate, setPropagate] = useState<boolean>(false)
  const [replace, setReplace] = useState<boolean>(true)
  const [reason, setReason] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [reasonCategory, setReasonCategory] = useState<string | null>(null)
  const [notifyWorkers, setNotifyWorkers] = useState<boolean>(true)
  const { policyWindow, isStandard } = useCancellationPolicy(
    shift as TimelineShift
  )
  const shouldEnableLTActions = useGateValue(FeatureGates.EnableLTAActions)
  const cancellationPolicyWindows =
    shift?.cancellationPolicy?.policyWindows ||
    DEFAULT_CANCELLATION_POLICY_WINDOW
  const [showInfielderSettings, setShowInfielderSettings] = useState(false)

  const shiftStartDate =
    shift?.startsAt &&
    formatInTimeZone(
      shift.startsAt,
      getLocationTimezone(shift.location),
      'EEEE, MMMM d'
    )

  const { assignment } = useAssignmentDetailContextStateValue()
  const hasOrientation = assignment?.hasOrientation
  const isRemovingOrientationShift = shift?.isOrientation
  const shouldShowOrientationWarning =
    hasOrientation && !isRemovingOrientationShift

  if (!worker?.id || !shift?.id) return null

  const removeWorkerMutation = useRemoveWorker({
    assignmentId: assignmentId,
    workerId: worker.id,
    shiftId: shift.id,
    onError: (error) => {
      setError(error.message)
    },
  })

  if (!removeWorkerMutation) return null

  const handleSubmitClick = async () => {
    try {
      await removeWorkerMutation.mutateAsync({
        propagate: propagate ?? false,
        replace: replace ?? false,
        reason_category: reasonCategory ?? '',
        reason: reason,
        should_notify: notifyWorkers,
      })
      onClose()
      window.location.reload()
    } catch (error) {
      // Error handling will be managed by the mutation
    }
  }

  return (
    <Box>
      <Heading level={2} mb={4}>
        Remove {worker?.name}
      </Heading>
      {shouldShowOrientationWarning ? <OrientationWarningMessage /> : null}

      <Text fontWeight="bold" mb={1}>
        Should we replace {worker?.name}?
      </Text>
      <RadioGroup
        options={[
          {
            label:
              'Replace (We’ll remove this worker and immediately find a replacement)',
            value: 'replace',
          },
          {
            label: `Do Not Replace (Will reduce headcount by 1)`,
            value: 'no_replace',
          },
        ]}
        name="replace"
        value={replace ? 'replace' : 'no_replace'}
        onChange={(value) => {
          setReplace(value === 'replace')
        }}
      />

      <Text fontWeight="bold" mt={4} mb={1}>
        Where should we apply these changes?
      </Text>
      <RadioGroup
        options={[
          {
            label: 'This shift only',
            value: 'this_shift_only',
          },
          {
            label: `All shifts in this assignment on and after ${shiftStartDate}`,
            value: 'apply_to_other_shifts',
          },
        ]}
        name="propagate"
        value={propagate ? 'apply_to_other_shifts' : 'this_shift_only'}
        onChange={(value) => setPropagate(value !== 'this_shift_only')}
      />

      <Text fontWeight="bold" mt={4} mb={1}>
        Why are you removing this worker?
      </Text>
      <Select
        options={WORKER_TERMINATION_REASONS}
        value={
          WORKER_TERMINATION_REASONS.find(
            (option) => option.value === reasonCategory
          ) || null
        }
        aria-label="Select a removal reason category"
        onChange={(option) =>
          setReasonCategory(
            (option as (typeof WORKER_TERMINATION_REASONS)[number]).value
          )
        }
      />

      <Box mt={2}>
        <Textarea
          minRows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={
            'Please provide more detail (e.g., headcount reduced for this shift, misconduct)'
          }
        />
        <Text
          color={'lightText'}
          fontSize={'12px'}
          lineHeight={'18px'}
          mt={1}
          textAlign={'right'}
        >
          {reason.trim().length} characters (minimum {MIN_REASON_CHARACTERS}{' '}
          characters)
        </Text>
      </Box>

      {policyWindow ? (
        <>
          <Message variant={'warning'} mb={3} mt={3}>
            Shift is within {policyWindow.cutoffTimeHours} hours cancellation
            window, you will be charged{' '}
            {formatPolicyWindowRemoveWorkerCharge(policyWindow, shift)}.
          </Message>
          <Box mb={3}>
            <Text fontWeight="bold" mb={2}>
              Your cancellation policy is as follows:
            </Text>
            <ul
              style={{
                listStyle: 'disc',
                marginLeft: '1em',
                marginBottom: '1em',
              }}
            >
              {cancellationPolicyWindows.map((policy) => {
                return (
                  <li key={getPolicyWindowKey(policy)}>
                    Cancellations made within {policy.cutoffTimeHours} hours of
                    the shift start time will be charged{' '}
                    {formatPolicyWindowListCharge(policy)}.
                  </li>
                )
              })}
            </ul>
          </Box>
          {isStandard ? (
            <Box mt={3}>
              <BaseLink
                onClick={() => {
                  window.open(policyUrl, '_blank')
                }}
                href={policyUrl}
                target={'_blank'}
                rel="noreferrer"
              >
                See policy
              </BaseLink>
            </Box>
          ) : null}
        </>
      ) : null}
      {shouldEnableLTActions ? (
        <Box mt={2}>
          <Flex alignItems="center" gap={2}>
            <Settings size={16} />
            <Text fontWeight="bold">WorkWhile Infielder Settings</Text>
            <Button
              variant="text"
              onClick={() => setShowInfielderSettings(!showInfielderSettings)}
            >
              {showInfielderSettings ? 'Hide' : 'Show'}
            </Button>
          </Flex>

          {showInfielderSettings ? (
            <Box mt={2}>
              <Text color="lightText" mb={2}>
                Only internal WorkWhile HQ users can see this option
              </Text>
              <Checkbox
                value={Boolean(notifyWorkers)}
                onChange={(checked) => setNotifyWorkers(checked)}
              >
                Notify {worker?.name} about this removal
              </Checkbox>
            </Box>
          ) : null}
        </Box>
      ) : (
        <Message variant="fixed" mt={3} bg="#f5f5f5">
          {worker?.name} will be notified that the{' '}
          {propagate ? 'shifts have' : 'shift has'} been removed from their
          schedule.
        </Message>
      )}
      {error ? (
        <Message variant="error" mt={3}>
          {error}
        </Message>
      ) : null}
      <Flex justifyContent="flex-end" gap={2} mt={3}>
        <Button
          onClick={handleSubmitClick}
          disabled={
            reason.length < MIN_REASON_CHARACTERS ||
            reasonCategory === null ||
            propagate === null ||
            replace === null
          }
          loading={removeWorkerMutation.isPending}
        >
          Submit
        </Button>
      </Flex>
    </Box>
  )
}
